import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box, Button, FormField, Header, Input, Modal, SpaceBetween, Textarea } from '@amzn/awsui-components-react-v3';
import { RegisterSsoGroupRequest } from 'aws-sdk/clients/omnismusservice';
import { registerSsoGroup } from 'src/api/omnismus';
import { GroupEntry } from '../utils/smusUtil';

export interface RegisterSsoGroupFormProps {
  visible: boolean;
  onDismiss: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
  /** When set, the form operates in edit mode with prepopulated fields. */
  editGroup?: GroupEntry;
}

export const RegisterSsoGroupForm = (props: RegisterSsoGroupFormProps) => {
  const isEditMode = !!props.editGroup;

  const [loading, setLoading] = useState(false);
  const [teamId, setTeamId] = useState('');
  const [teamName, setTeamName] = useState('');
  const [email, setEmail] = useState('');
  const [wiki, setWiki] = useState('');
  const [resolverGroup, setResolverGroup] = useState('');
  const [useCaseDescription, setUseCaseDescription] = useState('');
  const [ctiCategory, setCtiCategory] = useState('');
  const [ctiType, setCtiType] = useState('');
  const [ctiItem, setCtiItem] = useState('');

  // Prepopulate when editGroup changes or modal becomes visible
  useEffect(() => {
    if (props.visible && props.editGroup) {
      const group = props.editGroup;
      setTeamId(group.teamId ?? '');
      setTeamName(group.name ?? '');
      setEmail(group.email ?? '');
      setWiki(group.wiki ?? '');
      setResolverGroup(group.resolverGroup ?? '');
      setUseCaseDescription(group.useCaseDescription ?? '');
      setCtiCategory(group.primaryCti?.category ?? '');
      setCtiType(group.primaryCti?.type ?? '');
      setCtiItem(group.primaryCti?.item ?? '');
    } else if (props.visible && !props.editGroup) {
      // Reset for create mode
      setTeamId('');
      setTeamName('');
      setEmail('');
      setWiki('');
      setResolverGroup('');
      setUseCaseDescription('');
      setCtiCategory('');
      setCtiType('');
      setCtiItem('');
    }
  }, [props.visible, props.editGroup]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const request: RegisterSsoGroupRequest = {
        teamId,
        teamName,
        teamInfo: {
          email,
          wiki,
          resolverGroup,
          useCaseDescription,
          primaryCti: { category: ctiCategory, type: ctiType, item: ctiItem },
        },
      };
      await registerSsoGroup(request);
      props.onSuccess();
    } catch (e) {
      props.onError(e instanceof Error ? e.message : String(e));
    }
    setLoading(false);
  };

  const isSubmitDisabled =
    !teamId ||
    !teamName ||
    !email ||
    !wiki ||
    !resolverGroup ||
    !useCaseDescription ||
    !ctiCategory ||
    !ctiType ||
    !ctiItem;

  return (
    <Modal
      visible={props.visible}
      onDismiss={props.onDismiss}
      header={<Header variant='h2'>{isEditMode ? 'Edit SSO group' : 'Register SSO group'}</Header>}
      footer={
        <Box float='right'>
          <SpaceBetween size='xs' direction='horizontal'>
            <Button variant='link' onClick={props.onDismiss}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleSubmit} loading={loading} disabled={isSubmitDisabled}>
              {isEditMode ? 'Save' : 'Register'}
            </Button>
          </SpaceBetween>
        </Box>
      }
      size='large'
    >
      <form style={{ width: '100%' }}>
        <SpaceBetween size='l'>
          <FormField
            label='Team ID'
            description='Unique permissions team identifier.'
            constraintText='Required'
            stretch={true}
          >
            <Input
              value={teamId}
              onChange={({ detail }) => setTeamId(detail.value)}
              placeholder='e.g. amzn1.abacus.team.xxxxx'
              disabled={isEditMode}
            />
          </FormField>
          <FormField
            label='Team name'
            description='Unique permissions team name.'
            constraintText='Required'
            stretch={true}
          >
            <Input
              value={teamName}
              onChange={({ detail }) => setTeamName(detail.value)}
              placeholder='e.g. My Team'
              disabled={isEditMode}
            />
          </FormField>
          <FormField label='Email' description='Team contact email address.' constraintText='Required' stretch={true}>
            <Input
              value={email}
              onChange={({ detail }) => setEmail(detail.value)}
              placeholder='e.g. team@amazon.com'
              inputMode='email'
            />
          </FormField>
          <FormField label='Wiki' description='Link to the team wiki page.' constraintText='Required' stretch={true}>
            <Input
              value={wiki}
              onChange={({ detail }) => setWiki(detail.value)}
              placeholder='e.g. https://w.amazon.com/bin/view/MyTeam'
            />
          </FormField>
          <FormField label='CTI category' constraintText='Required' stretch={true}>
            <Input value={ctiCategory} onChange={({ detail }) => setCtiCategory(detail.value)} placeholder='e.g. AWS' />
          </FormField>
          <FormField label='CTI type' constraintText='Required' stretch={true}>
            <Input value={ctiType} onChange={({ detail }) => setCtiType(detail.value)} placeholder='e.g. DataLake' />
          </FormField>
          <FormField label='CTI item' constraintText='Required' stretch={true}>
            <Input value={ctiItem} onChange={({ detail }) => setCtiItem(detail.value)} placeholder='e.g. SMUS' />
          </FormField>
          <FormField
            label='Resolver group'
            description='The resolver group for the team.'
            constraintText='Required'
            stretch={true}
          >
            <Input
              value={resolverGroup}
              onChange={({ detail }) => setResolverGroup(detail.value)}
              placeholder='e.g. my-team-oncall'
            />
          </FormField>
          <FormField
            label='Use case description'
            description='Describe the use case for this SSO group.'
            constraintText='Required'
            stretch={true}
          >
            <Textarea
              value={useCaseDescription}
              onChange={({ detail }) => setUseCaseDescription(detail.value)}
              placeholder='Describe the intended use case...'
              rows={3}
            />
          </FormField>
        </SpaceBetween>
      </form>
    </Modal>
  );
};
